import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Camera, CheckCircle, Circle, ClipboardText, StopCircle, Warning, X,
} from '@phosphor-icons/react';
import { itApi } from './itApi';

const REGION = 'it-walk-scan-region';

const SCAN_META = {
  ok: { label: '確認', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  misplaced: { label: '場所違い', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  unknown: { label: '不明コード', cls: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
  duplicate: { label: 'スキャン済', cls: 'bg-blue-50 text-blue-600 border-blue-200' },
};

// 棚卸ウォークパネル — QRスキャンでフロアの資産を巡回確認する。
export default function WalkPanel({ walk, setWalk, pinned, onFinished, onCancel }) {
  const [manual, setManual] = useState('');
  const [scanning, setScanning] = useState(false);
  const [last, setLast] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const scannerRef = useRef(null);
  const lastCodeRef = useRef({ code: '', at: 0 });

  const scans = walk.scans || [];
  const scannedIds = new Set(scans.filter((s) => s.asset_id).map((s) => s.asset_id));
  const okCount = scans.filter((s) => s.status === 'ok').length;
  const expectedCount = (walk.expected_asset_ids || []).length;
  const extras = scans.filter((s) => s.status !== 'ok');

  const doScan = async (raw) => {
    const c = (raw || '').trim();
    if (!c) return;
    const now = Date.now();
    if (lastCodeRef.current.code === c && now - lastCodeRef.current.at < 3000) return;
    lastCodeRef.current = { code: c, at: now };
    try {
      const r = await itApi.walkScan(walk.id, c);
      setLast({ ...r, code: c });
      if (r.scan) setWalk((w) => ({ ...w, scans: [...(w.scans || []), r.scan] }));
      if (r.status === 'ok') toast.success(`確認: ${r.asset?.name || c}`);
      else if (r.status === 'misplaced') toast.warning(`場所違い: ${r.asset?.name || c}`);
      else if (r.status === 'duplicate') toast.info(`スキャン済み: ${r.asset?.name || c}`);
      else toast.error('該当する資産が見つかりません');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'スキャンに失敗しました');
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScan = async () => {
    setScanning(true);
    try {
      const mod = await import('html5-qrcode');
      const inst = new mod.Html5Qrcode(REGION);
      scannerRef.current = inst;
      await inst.start({ facingMode: 'environment' }, { fps: 10, qrbox: 200 },
        (decoded) => { doScan(decoded); },
        () => { /* per-frame errors ignored */ });
    } catch {
      setScanning(false);
      toast.error('カメラを起動できませんでした — 手入力をご利用ください');
    }
  };

  useEffect(() => () => { stopScan(); }, []); // eslint-disable-line

  const finish = async () => {
    setFinishing(true);
    try {
      const r = await itApi.walkFinish(walk.id);
      await stopScan();
      onFinished(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '棚卸の完了に失敗しました');
    } finally { setFinishing(false); }
  };

  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/30 p-3 space-y-3" data-testid="it-walk-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-bold text-emerald-800 flex items-center gap-1">
            <ClipboardText size={14} weight="duotone" /> 棚卸モード
          </p>
          <p className="text-[10px] text-zinc-500 mt-0.5">QRラベルをスキャンして資産を確認</p>
        </div>
        <button type="button" className="text-zinc-400 hover:text-zinc-600" title="中断（進行状況は保持されます）"
          onClick={async () => { await stopScan(); onCancel(); }} data-testid="it-walk-cancel">
          <X size={14} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-zinc-200 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all"
            style={{ width: `${expectedCount ? (okCount / expectedCount) * 100 : 0}%` }} />
        </div>
        <span className="text-xs font-bold text-emerald-700 tabular-nums shrink-0" data-testid="it-walk-progress">
          {okCount} / {expectedCount}
        </span>
      </div>

      <div id={REGION} className={scanning ? 'rounded-lg overflow-hidden' : 'hidden'} />
      {scanning ? (
        <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={stopScan} data-testid="it-walk-stop-camera">
          <StopCircle size={14} className="mr-1" /> カメラを停止
        </Button>
      ) : (
        <Button size="sm" className="w-full h-8 text-xs bg-teal-700 hover:bg-teal-800" onClick={startScan} data-testid="it-walk-start-camera">
          <Camera size={14} className="mr-1" /> カメラでスキャン
        </Button>
      )}

      <div className="flex gap-1.5">
        <Input value={manual} onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { doScan(manual); setManual(''); } }}
          placeholder="資産番号を手入力…" className="h-8 text-xs bg-white" data-testid="it-walk-manual-input" />
        <Button variant="outline" size="sm" className="h-8 px-2 text-xs shrink-0"
          onClick={() => { doScan(manual); setManual(''); }} data-testid="it-walk-manual-submit">
          確認
        </Button>
      </div>

      {last && (
        <div className={`rounded-md border px-2 py-1 text-[11px] font-semibold ${SCAN_META[last.status]?.cls || SCAN_META.unknown.cls}`} data-testid="it-walk-last-scan">
          {SCAN_META[last.status]?.label}: {last.asset?.name || last.code}
        </div>
      )}

      <div className="space-y-1 max-h-52 overflow-y-auto" data-testid="it-walk-checklist">
        {pinned.map((a) => {
          const done = scannedIds.has(a.id);
          return (
            <div key={a.id} className="flex items-center gap-1.5 text-[11px]" data-testid={`it-walk-item-${a.id}`}>
              {done
                ? <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
                : <Circle size={14} className="text-zinc-300 shrink-0" />}
              <span className={`truncate ${done ? 'text-zinc-700 font-semibold' : 'text-zinc-500'}`}>{a.name}</span>
              {a.asset_tag && <span className="font-mono text-[9px] text-zinc-400 shrink-0">{a.asset_tag}</span>}
            </div>
          );
        })}
        {extras.map((s, i) => (
          <div key={`x-${i}`} className="flex items-center gap-1.5 text-[11px]">
            <Warning size={14} weight="fill" className={s.status === 'misplaced' ? 'text-amber-500 shrink-0' : 'text-zinc-400 shrink-0'} />
            <span className="truncate text-zinc-600">{s.asset_name || s.code}</span>
            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${SCAN_META[s.status]?.cls}`}>{SCAN_META[s.status]?.label}</Badge>
          </div>
        ))}
      </div>

      <Button size="sm" className="w-full h-8 text-xs bg-zinc-800 hover:bg-zinc-700" disabled={finishing}
        onClick={finish} data-testid="it-walk-finish">
        棚卸を完了する
      </Button>
    </div>
  );
}
